import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '@/lib/api';
import { RevealOnScroll } from './Motion';
import { toast } from 'sonner';

export default function AiAssistant() {
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [loading, setLoading] = useState(false);
    const abortRef = useRef(null);

    const solve = async (e) => {
        e?.preventDefault();
        if (!question.trim()) {
            toast.error('الصق سؤال الاستطلاع أولًا');
            return;
        }
        setLoading(true);
        setAnswer('');
        try {
            const controller = new AbortController();
            abortRef.current = controller;
            const res = await fetch(`${API}/ai/solve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question }),
                signal: controller.signal,
            });
            if (!res.ok || !res.body) throw new Error(`فشل الاتصال (${res.status})`);
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const frames = buffer.split('\n\n');
                buffer = frames.pop() || '';
                for (const frame of frames) {
                    const lines = frame.split('\n');
                    let event = 'message';
                    let data = '';
                    for (const line of lines) {
                        if (line.startsWith('event:')) event = line.slice(6).trim();
                        else if (line.startsWith('data:')) data += line.slice(5);
                    }
                    if (event === 'done') return;
                    if (event === 'error') {
                        toast.error(data || 'حدث خطأ');
                        return;
                    }
                    if (event === 'message' && data) {
                        setAnswer((a) => a + data);
                    }
                }
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                toast.error(err.message || 'حدث خطأ');
            }
        } finally {
            setLoading(false);
            abortRef.current = null;
        }
    };

    const stop = () => {
        abortRef.current?.abort();
        setLoading(false);
    };

    const copyAnswer = async () => {
        try {
            await navigator.clipboard.writeText(answer);
            toast.success('تم نسخ الإجابة');
        } catch {
            toast.error('تعذّر النسخ');
        }
    };

    const clear = () => {
        setQuestion('');
        setAnswer('');
    };

    return (
        <section
            id="ai-assistant"
            className="relative border-t border-ink/20 py-24 md:py-32 overflow-hidden"
            data-testid="ai-assistant-section"
        >
            {/* Emerald ambient glow */}
            <div
                aria-hidden
                className="absolute pointer-events-none rounded-full blur-3xl opacity-30"
                style={{
                    width: 600,
                    height: 600,
                    background: '#00A86B',
                    top: '15%',
                    insetInlineEnd: '-8%',
                }}
            />

            <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                <RevealOnScroll>
                    <div className="flex items-baseline justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] mb-4 text-emerald-signal">
                                <span className="w-8 h-px bg-emerald-signal" />
                                مساعد الذكاء الاصطناعي
                            </div>
                            <h2 className="font-display font-black text-4xl md:text-6xl leading-[1.05] tracking-tight">
                                حلّ الاستطلاع.<br />
                                <span className="italic font-light text-emerald-signal">
                                    بالذكاء الاصطناعي.
                                </span>
                            </h2>
                        </div>
                        <div className="max-w-sm text-ink-600 text-sm leading-relaxed">
                            الصق سؤال الاستطلاع في المربع الأخضر. سيقترح المساعد إجابة صادقة
                            ومختصرة باللغة العربية — ثم القرار لك دائمًا.
                        </div>
                    </div>
                </RevealOnScroll>

                <RevealOnScroll delay={0.1}>
                    <form
                        onSubmit={solve}
                        className="relative border-2 border-emerald-signal bg-emerald-signal/10 backdrop-blur-sm"
                        data-testid="ai-assistant-form"
                    >
                        <div className="flex items-center justify-between px-6 md:px-8 py-3 border-b-2 border-emerald-signal bg-emerald-signal text-ink text-[11px] uppercase tracking-widest font-medium">
                            <span>الصق سؤال الاستطلاع هنا</span>
                            <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-ink animate-pulse" />
                                Claude Sonnet 4.6
                            </span>
                        </div>

                        <textarea
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="مثال: ما رأيك في منتج القهوة الجديد X؟ اختر: ممتاز / جيد / متوسط / سيء…"
                            rows={4}
                            className="w-full bg-transparent px-6 md:px-8 py-6 text-base md:text-lg text-ink placeholder:text-ink/40 focus:outline-none resize-none font-sans"
                            data-testid="ai-assistant-input"
                        />

                        <div className="flex flex-wrap items-center justify-between gap-3 px-6 md:px-8 py-4 border-t-2 border-emerald-signal bg-sand-100">
                            <div className="text-[11px] uppercase tracking-widest text-ink-400">
                                {question.length} حرف
                            </div>
                            <div className="flex gap-3">
                                {(question || answer) && !loading && (
                                    <button
                                        type="button"
                                        onClick={clear}
                                        className="text-sm px-4 py-2.5 border border-ink hover:bg-ink hover:text-sand-100 transition-colors"
                                        data-testid="ai-assistant-clear"
                                    >
                                        مسح
                                    </button>
                                )}
                                {loading ? (
                                    <button
                                        type="button"
                                        onClick={stop}
                                        className="text-sm px-6 py-2.5 bg-ink text-sand-100 hover:bg-red-600 transition-colors"
                                        data-testid="ai-assistant-stop"
                                    >
                                        إيقاف
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="text-sm px-6 py-2.5 bg-emerald-signal text-ink font-medium hover:bg-ink hover:text-emerald-signal transition-colors hover-arrow inline-flex items-center gap-2"
                                        data-testid="ai-assistant-submit"
                                    >
                                        احصل على الإجابة
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path
                                                d="M13 7L1 7M1 7L7 1M1 7L7 13"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </RevealOnScroll>

                <AnimatePresence>
                    {(answer || loading) && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-6 border border-ink bg-sand-100"
                            data-testid="ai-assistant-answer"
                        >
                            <div className="flex items-center justify-between px-6 md:px-8 py-3 border-b border-ink text-[11px] uppercase tracking-widest">
                                <span>الإجابة المقترحة</span>
                                <div className="flex items-center gap-4">
                                    {loading && (
                                        <span className="flex items-center gap-2 text-emerald-signal">
                                            <span className="w-2 h-2 rounded-full bg-emerald-signal animate-pulse" />
                                            جاري التفكير…
                                        </span>
                                    )}
                                    {!loading && answer && (
                                        <button
                                            type="button"
                                            onClick={copyAnswer}
                                            className="text-emerald-signal hover:underline"
                                            data-testid="ai-assistant-copy"
                                        >
                                            نسخ
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="px-6 md:px-8 py-6 text-base md:text-lg leading-relaxed whitespace-pre-wrap font-sans">
                                {answer || (
                                    <span className="text-ink-400 italic">
                                        بانتظار الرد من المساعد…
                                    </span>
                                )}
                                {loading && answer && (
                                    <span className="inline-block w-2 h-5 bg-emerald-signal align-middle ms-1 animate-pulse" />
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="mt-6 text-xs text-ink-400 leading-relaxed max-w-2xl">
                    ⓘ المساعد يقدّم اقتراحًا فقط. راجِع الإجابة قبل إرسالها للحفاظ على
                    مصداقية ملفك الشخصي في منصّات الاستطلاعات.
                </div>
            </div>
        </section>
    );
}
